import React, { useEffect, useRef, useState } from 'react';
import { Animated, StyleSheet, View, TouchableWithoutFeedback, Platform, Pressable } from 'react-native';
import { Button, useTheme } from 'react-native-paper';
import { BannerContent } from './bannerContent';
import { PanGestureHandler, State } from 'react-native-gesture-handler';

export const TopDrawer = ({ 
	isVisible, 
	onClose, 
	bannerDetails,
	swipeDownProgress = null,
	isInitialRender = false
}) => {
	const theme = useTheme();
	const SOLID_WHITE = '#FFFFFF';
	const translateY = useRef(new Animated.Value(-1000)).current;
	const gestureY = useRef(new Animated.Value(0)).current;
	const drawerRef = useRef(null);
	const [drawerHeight, setDrawerHeight] = useState(300); // Default height
	const [internalInitialRender, setInternalInitialRender] = useState(true);
	// Add a ref to track if an animation is in progress
	const animationInProgress = useRef(false);

	// Clamp the gesture movement to prevent pulling down too far
	const clampedTranslateY = gestureY.interpolate({
		inputRange: [-1000, 0, 100],
		outputRange: [-1000, 0, 30], // Less elasticity to prevent visual jumps
		extrapolate: 'clamp'
	});

	// Create a single source of truth for drawer position
	// by handling translateY and gestureY in a consistent way
	useEffect(() => {
		// Set initial position without animation on first render
		if (isInitialRender || internalInitialRender) {
			translateY.setValue(isVisible ? 0 : -1000);
			gestureY.setValue(0);
			setInternalInitialRender(false);
			return;
		}

		// Always reset gesture value when visibility changes
		gestureY.setValue(0);
		
		// Cancel any existing animations for clean state
		translateY.stopAnimation();

		if (isVisible) {
			// Show drawer with animation
			Animated.spring(translateY, {
				toValue: 0,
				tension: 65,
				friction: 12,
				useNativeDriver: true,
			}).start();
		} else {
			// Hide drawer with simple animation - slightly faster duration for crisp feel
			Animated.timing(translateY, {
				toValue: -1000,
				duration: 230,
				useNativeDriver: true,
			}).start();
		}
	}, [isVisible, translateY, gestureY, isInitialRender, internalInitialRender]);

	// Function to handle closing with animation
	const closeWithAnimation = () => {
		if (!isVisible || animationInProgress.current) return;
		// Immediately call onClose to update parent state
		onClose();
		
		// We don't need to animate here since the isVisible useEffect will handle it
	};

	// Handle swipe down progress from header
	useEffect(() => {
		if (isInitialRender || internalInitialRender || isVisible) return; // Skip if visible or during initial render
		
		if (swipeDownProgress) {
			const progress = swipeDownProgress.__getValue();
			
			if (progress > 0) {
				// Only update position if we're in a swipe-down gesture
				const dragPosition = -drawerHeight * (1 - progress);
				translateY.setValue(dragPosition);
			}
		}
	}, [swipeDownProgress, isVisible, translateY, drawerHeight, isInitialRender, internalInitialRender]);

	const handleGestureEvent = Animated.event(
		[{ nativeEvent: { translationY: gestureY } }],
		{ 
			useNativeDriver: true,
			// Add listener for smoother transition when closing
			listener: (event) => {
				// Keep drawer slightly closed during gesture to prevent flash on close
				if (event.nativeEvent.translationY < -30) {
					// If swiping up significantly, keep translateY slightly negative
					// This prevents the full open flash during close transition
					translateY.setValue(-10);
				}
			}
		}
	);

	// Calculate combined translation for the drawer position 
	// with a clamped result to ensure smooth movement
	const combinedTranslateY = Animated.add(translateY, clampedTranslateY).interpolate({
		inputRange: [-1000, -1, 0, 1000],
		outputRange: [-1000, -1, 0, 1000],
		extrapolate: 'clamp'
	});
	
	// Calculate overlay opacity based on drawer position and swipe down progress
	const overlayOpacity = Animated.add(
		combinedTranslateY.interpolate({
			inputRange: [-1000, -800, -400, -200, 0],
			outputRange: [0, 0.1, 0.3, 0.4, 0.5], // Gradually increase opacity as drawer opens
			extrapolate: 'clamp'
		}),
		swipeDownProgress ? swipeDownProgress.interpolate({
			inputRange: [0, 1],
			outputRange: [0, 0.25], // Add extra opacity for swipe down
			extrapolate: 'clamp'
		}) : new Animated.Value(0)
	);

	const handleSwipeStateChange = (event) => {
		if (!isVisible) return;
		
		const { translationY, velocityY, state } = event.nativeEvent;
		
		if (state === State.END) {
			// Consider both distance and velocity for a more natural feel
			if (translationY < -50 || velocityY < -200) {
				// Swiped up past threshold or with a quick flick, close drawer
				// Just call onClose directly - this is what worked before
				onClose();
			} else {
				// Spring back to fully open position - reset gesture first
				gestureY.setValue(0);
				Animated.spring(translateY, {
					toValue: 0,
					tension: 65,
					friction: 12,
					useNativeDriver: true,
				}).start();
			}
		}
	};

	const styles = StyleSheet.create({
		overlay: {
			position: 'absolute',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			backgroundColor: theme.colors.background,
			zIndex: 9, // Just below the drawer
			borderTopRightRadius: Platform.OS === 'web' ? 15 : 0,
			borderTopLeftRadius: Platform.OS === 'web' ? 15 : 0,
		},
		drawer: {
			position: 'absolute',
			left: 0,
			right: 0,
			zIndex: 11,
			borderTopRightRadius: Platform.OS === 'web' ? 15 : 0,
			borderTopLeftRadius: Platform.OS === 'web' ? 15 : 0,
			borderBottomLeftRadius: 12,
			borderBottomRightRadius: 12,
			shadowOffset: {
				width: 0,
				height: 2,
			},
			shadowOpacity: 0.25,
			shadowRadius: 3.84,
			elevation: 5,
			top: 0
		},
		closeBanner: {
			width: 48,
			height: 4,
			margin: 'auto',
			marginTop: 8,
			marginBottom: 16, // Increased bottom margin for better spacing with rounded corners
			opacity: 0.5,
			alignSelf: 'center',
		},
		content: {
			flex: 1,
		}
	});

	return (
		<>
			{!(isInitialRender || internalInitialRender) && (
				<TouchableWithoutFeedback onPress={closeWithAnimation}>
					<Animated.View
						style={[
							styles.overlay,
							{
								opacity: overlayOpacity,
								// Only capture touch events when the drawer is open enough to be interacted with
								pointerEvents: (isVisible || (swipeDownProgress && swipeDownProgress.__getValue() > 0.3))
									? 'auto' 
									: 'none'
							},
						]}
					/>
				</TouchableWithoutFeedback>
			)}
			<Pressable onPress={(e) => e.stopPropagation()} style={{ zIndex: 11, cursor: 'auto' }}>
				<Animated.View
					style={[
						styles.drawer,
						{
							backgroundColor: theme.colors.white,
							shadowColor: theme.colors.drawerShadow,
							transform: [
								{ 
									translateY: combinedTranslateY
								}
							],
							// Hide completely during initial render to prevent flicker
							opacity: (isInitialRender || internalInitialRender) ? 0 : 1
						},
					]}
					ref={drawerRef}
					onLayout={(event) => {
					// Measure the drawer height when it renders
						const { height } = event.nativeEvent.layout;
						if (height > 0) {
							setDrawerHeight(height);
						}
					}}
				>
					<PanGestureHandler
						onGestureEvent={handleGestureEvent}
						onHandlerStateChange={handleSwipeStateChange}
					>
						<Animated.View>
							<View style={styles.content}>
								{bannerDetails && bannerDetails.length > 0 && (
									<BannerContent 
										displayArr={bannerDetails}
										onClose={onClose}
									/>
								)}
							</View>
							<View style={{ 
								backgroundColor: SOLID_WHITE,
								borderBottomLeftRadius: 12,
								borderBottomRightRadius: 12
							}}>
								<Button 
									style={[styles.closeBanner, { backgroundColor: theme.colors.drawerHandle }]} 
									onPress={closeWithAnimation}
								/>
							</View>
						</Animated.View>
					</PanGestureHandler>
				</Animated.View>
			</Pressable>
		</>
	);
};
